/**
 * ad-entry — 유입 출처 첫 요청 캡처 (CL fix_7 §1 · 레인 0b). 순수 TS — Edge 미들웨어·브라우저·vitest 공용.
 *
 * ── 왜 ──
 * 리드 채널은 Apps Script 가 «제출 시점» `payload.query`(= 그 순간의 location.search)로 판정한다.
 * 광고로 착지한 사람이 다른 페이지로 옮겨 가서 폼을 내면 n_* 파라미터가 사라지고 organic 으로 남는다.
 * 그래서 «첫 요청» 에서 출처를 잡아 쿠키(30분)와 sessionStorage 에 같은 값으로 들고 다닌다.
 *
 * ── 계약 (레인 A 와 공유 — 바꾸지 말 것) ──
 *   쿠키   `kd_entry` · Max-Age 1800 · Path=/ · SameSite=Lax
 *   값     encodeURIComponent(JSON.stringify({ src, n_media?, n_query?, utm_source?, ts }))
 *          src = 'naver_ad' | 'organic' | 'cafe' | 'kakao' · ts = epoch ms
 *   미러   sessionStorage `kd_entry` 에 «같은 문자열»
 *   판정   utm_source=naver_ad 또는 n_media/n_query 존재 → naver_ad
 *   보존   이미 naver_ad 가 있으면 30분 안에는 덮어쓰지 않는다.
 *
 * ⛔ 서버 렌더에서 이 쿠키를 읽지 않는다. 상세·지역은 ISR 이라 cookies() 를 부르는 순간 라우트가 동적이 된다.
 *    읽기는 클라이언트(`./client`)에서만 한다. 미들웨어는 «쓰기만» 한다(Vary 추가 없음 — 렌더가 쿠키에 의존하지 않으므로).
 */

export const KD_ENTRY_KEY = 'kd_entry';
export const KD_ENTRY_TTL_MS = 30 * 60 * 1000;
export const KD_ENTRY_MAX_AGE_S = 30 * 60;

export const ENTRY_SOURCES = ['naver_ad', 'organic', 'cafe', 'kakao'] as const;
export type EntrySource = (typeof ENTRY_SOURCES)[number];

export type EntryValue = {
  src: EntrySource;
  n_media?: string;
  n_query?: string;
  utm_source?: string;
  ts: number;
};

const MAX_VAL = 100;

export function isEntrySource(v: unknown): v is EntrySource {
  return typeof v === 'string' && (ENTRY_SOURCES as readonly string[]).includes(v);
}

type ParamReader = { get(name: string): string | null };

function toReader(search: string | ParamReader | null | undefined): ParamReader | null {
  if (search == null) return null;
  if (typeof search !== 'string') return search;
  const q = search.charAt(0) === '?' ? search.slice(1) : search;
  if (!q) return null;
  try {
    return new URLSearchParams(q);
  } catch {
    return null;
  }
}

function clip(v: string | null | undefined): string | undefined {
  if (v == null) return undefined;
  const t = String(v).trim();
  if (!t) return undefined;
  // 글자 단위로 자른다(서로게이트 반토막 방지 — Array.from 은 코드포인트 단위).
  return Array.from(t).slice(0, MAX_VAL).join('');
}

function refHost(referrer: string | null | undefined): string {
  if (!referrer) return '';
  try {
    return new URL(referrer).hostname.toLowerCase();
  } catch {
    return '';
  }
}

const CAFE_HOST_RE = /(^|\.)cafe\.naver\.com$/;
// 카카오 «로그인 왕복»(kauth·accounts)은 유입이 아니라 우리 사이트의 OAuth 복귀다 — 제외.
const KAKAO_HOST_RE = /(^|\.)(kakao\.com|kakaocdn\.net|daum\.net)$/;
const KAKAO_AUTH_HOST_RE = /^(kauth|accounts)\.kakao\.com$/;

/**
 * 요청 하나에서 «신호» 를 읽는다. 신호가 없으면 null(= 아무것도 바꾸지 않는다).
 *   naver_ad  utm_source=naver_ad 또는 n_media·n_query 중 하나라도 있음
 *   cafe      utm_source 에 cafe 포함, 또는 리퍼러가 cafe.naver.com
 *   kakao     utm_source 에 kakao 포함, 또는 리퍼러가 카카오(로그인 왕복 제외)
 *   organic   외부 리퍼러(우리 도메인 아님)가 있고 위 셋이 아님
 * ⚠️ 내부 이동(리퍼러가 selfHost)·리퍼러 없음은 신호가 아니다 — 첫 출처를 지우지 않는다.
 */
export function classifyEntry(
  search: string | ParamReader | null | undefined,
  referrer: string | null | undefined,
  now: number,
  selfHost?: string | null,
): EntryValue | null {
  const sp = toReader(search);
  const utm = clip(sp?.get('utm_source'));
  const nMedia = clip(sp?.get('n_media'));
  const nQuery = clip(sp?.get('n_query'));
  const utmL = (utm ?? '').toLowerCase();

  const base = (src: EntrySource): EntryValue => {
    const v: EntryValue = { src, ts: now };
    if (nMedia) v.n_media = nMedia;
    if (nQuery) v.n_query = nQuery;
    if (utm) v.utm_source = utm;
    return v;
  };

  if (utmL === 'naver_ad' || nMedia !== undefined || nQuery !== undefined) return base('naver_ad');
  if (utmL.includes('cafe')) return base('cafe');
  if (utmL.includes('kakao')) return base('kakao');

  const host = refHost(referrer);
  const self = (selfHost ?? '').toLowerCase().replace(/^www\./, '');
  const isSelf = !!host && !!self && (host === self || host.endsWith('.' + self));
  if (host && !isSelf) {
    if (CAFE_HOST_RE.test(host)) return base('cafe');
    if (KAKAO_HOST_RE.test(host) && !KAKAO_AUTH_HOST_RE.test(host)) return base('kakao');
    return base('organic');
  }
  // utm 이 있지만 위 셋이 아닌 경우(예: leadform_done)도 «외부 유입 신호» 로 보지 않는다 — 내부 링크 태그가 섞여 있다.
  return null;
}

export function serializeEntry(v: EntryValue): string {
  return encodeURIComponent(JSON.stringify(v));
}

/** 쿠키·sessionStorage 원문 → 값. 깨졌거나 계약 밖이면 null. 이중 인코딩·미인코딩 JSON 도 받아 준다. */
export function parseEntry(raw: string | null | undefined): EntryValue | null {
  if (!raw) return null;
  let s = String(raw);
  for (let i = 0; i < 2 && s.charAt(0) === '%'; i++) {
    try {
      s = decodeURIComponent(s);
    } catch {
      return null;
    }
  }
  let o: unknown;
  try {
    o = JSON.parse(s);
  } catch {
    return null;
  }
  if (!o || typeof o !== 'object') return null;
  const r = o as Record<string, unknown>;
  if (!isEntrySource(r.src)) return null;
  const ts = Number(r.ts);
  if (!Number.isFinite(ts) || ts <= 0) return null;
  const v: EntryValue = { src: r.src, ts };
  for (const k of ['n_media', 'n_query', 'utm_source'] as const) {
    const c = typeof r[k] === 'string' ? clip(r[k] as string) : undefined;
    if (c) v[k] = c;
  }
  return v;
}

export function isFresh(v: EntryValue | null | undefined, now: number): boolean {
  return !!v && now - v.ts >= 0 && now - v.ts < KD_ENTRY_TTL_MS;
}

/**
 * 새 값을 써야 하나. 반환값이 null 이면 아무것도 쓰지 않는다.
 *   - 신호 없음 → null
 *   - 30분 안의 naver_ad 가 있고 새 신호가 naver_ad 가 아님 → null(광고 귀속 보존)
 *   - 그 밖에는 새 신호를 쓴다(naver_ad → naver_ad 재클릭은 갱신 — 최신 n_query 가 맞다)
 */
export function decideEntry(existing: EntryValue | null, incoming: EntryValue | null, now: number): EntryValue | null {
  if (!incoming) return null;
  if (existing && existing.src === 'naver_ad' && isFresh(existing, now) && incoming.src !== 'naver_ad') return null;
  return incoming;
}

/**
 * 미들웨어용 — 이 요청에 Set-Cookie 를 붙여야 하면 그 값, 아니면 null.
 * ⚠️ organic 은 미들웨어가 쓰지 않는다(클라이언트가 쓴다). 신규 방문 «모든» 첫 요청에 Set-Cookie 가
 *    붙는 것을 피한다 — 광고·카페·카카오 착지(하루 수백 건)에만 붙는다.
 */
export function middlewareEntryCookie(
  cookieRaw: string | null | undefined,
  search: string | ParamReader | null | undefined,
  referrer: string | null | undefined,
  now: number,
  selfHost?: string | null,
): EntryValue | null {
  const incoming = classifyEntry(search, referrer, now, selfHost);
  if (!incoming || incoming.src === 'organic') return null;
  return decideEntry(parseEntry(cookieRaw), incoming, now);
}

/** 리드·이벤트에 실을 출처 문자열. 값이 없으면 'organic'(직접 유입 포함). */
export function entrySourceOf(v: EntryValue | null | undefined): EntrySource {
  return v?.src ?? 'organic';
}

/**
 * Set-Cookie 옵션(Next `response.cookies.set` 3번째 인자와 같은 모양).
 * httpOnly 가 아니다 — 클라이언트가 읽어 sessionStorage 에 미러하고 리드 payload 에 싣는다.
 */
export const KD_ENTRY_COOKIE_OPTIONS = {
  path: '/',
  maxAge: KD_ENTRY_MAX_AGE_S,
  sameSite: 'lax' as const,
  secure: true,
  httpOnly: false,
};

/** 광고 자동추적 파라미터(네이버) — 리드 query 복원 대상. facet.TRACKING_PARAM_RE 의 네이버 부분과 같은 목록. */
export const NAVER_AD_PARAM_RE = /^(n_media|n_query|n_rank|n_ad_group|n_ad_group_type|n_ad|n_keyword_id|n_keyword|n_campaign_type|n_campaign|n_match|NaPm)$/;
export const UTM_PARAM_RE = /^utm_(source|medium|campaign|content|term)$/;

/** 광고·utm 파라미터만 골라낸다(값은 100자 절단). 없으면 빈 객체. */
export function pickAttributionParams(search: string | ParamReader | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  const sp = toReader(search);
  if (!sp || typeof (sp as URLSearchParams).forEach !== 'function') return out;
  (sp as URLSearchParams).forEach((v, k) => {
    if (NAVER_AD_PARAM_RE.test(k) || UTM_PARAM_RE.test(k)) {
      const c = clip(v);
      if (c) out[k] = c;
    }
  });
  return out;
}

/**
 * 리드 payload.query 조립. 현재 URL 쿼리가 «우선» 이고, 현재 URL 에 광고·utm 파라미터가 하나도 없을 때만
 * 착지 때 보관한 파라미터를 채운다(섞지 않는다 — 두 출처를 섞으면 그룹 귀속이 거짓이 된다).
 * 출처 표식 `kd_entry` 는 항상 싣는다(Apps Script 는 query 키를 leads.utm jsonb 에 통째로 담는다 — 실측 cpl_gate).
 */
export function buildLeadQuery(
  current: Record<string, string>,
  stored: Record<string, string> | null | undefined,
  entry: EntryValue | null | undefined,
): Record<string, string> {
  const q: Record<string, string> = { ...current };
  const hasAttr = Object.keys(current).some((k) => NAVER_AD_PARAM_RE.test(k) || UTM_PARAM_RE.test(k));
  if (!hasAttr) {
    if (stored && Object.keys(stored).length) {
      for (const [k, v] of Object.entries(stored)) if (!(k in q)) q[k] = v;
    } else if (entry && entry.src === 'naver_ad') {
      // 보관된 전체 파라미터가 없으면(다른 탭·sessionStorage 불가) 쿠키 계약 필드라도 복원한다.
      if (entry.n_media) q.n_media = entry.n_media;
      if (entry.n_query) q.n_query = entry.n_query;
      if (entry.utm_source) q.utm_source = entry.utm_source;
    }
  }
  if (!('kd_entry' in q)) q.kd_entry = entrySourceOf(entry);
  return q;
}
